import { SharedAnimations } from './../../../shared/animations/animations';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { PatientNotes } from '@stockcare-models/IPatientNotes';
import { PatientService } from '@stockcare-services/patient/patient.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CreatePatientNoteDialog } from './create-patient-note-dialog/create-patient-note-dialog.component';
import { PatientNoteItem } from '@stockcare-models/PatientNoteItem';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { AuthenticationService } from '@stockcare-services/index';
import { NotificationService } from '@stockcare-services/notification-service/notification.service';
import { PatientNoteStorageService } from '@stockcare-storage/patient/patient-note-storage';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';

@Component({
  standalone: false,
  selector: 'app-create-patient-note',
  templateUrl: './create-patient-note.component.html',
  styleUrls: ['./create-patient-note.component.scss'],
  animations: [SharedAnimations],
})
export class CreatePatientNoteComponent implements OnInit, OnDestroy {
  @Input() patientName: string;
  @Input() patientCaseNumber: string;
  @Input() patientId: number;
  @Input() patientRoom: string = "";
  @Input() patientBed: string = "";
  patientNotes: PatientNotes[] = [];
  offlinePatientNotes: PatientNotes[] = [];
  dialogConfig: MatDialogConfig;

  constructor(
    private readonly patientService: PatientService,
    private readonly modalService: MatDialog,
    private readonly deviceService: DeviceService,
    private readonly authenticationService: AuthenticationService,
    private readonly notify: NotificationService,
    private readonly patientNoteStorageService: PatientNoteStorageService,
    private settingsService: SettingsService
  ) {}

  ngOnInit() {
    this.getPatientNotes();

    let enableCustomKeyboard = this.settingsService.enableCustomKeyboard();
    this.dialogConfig = (enableCustomKeyboard)
      ? {
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: 'calc(100vw - 10px)',
        height: 'calc(100vh - 65px)',
        position: {
          bottom: '5px',
          left: '5px',
          right: '5px',
          top: '60px',
        },
      }
      : {
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '95%',
        position: {
          top: '60px',
        },
      };
  }

  getPatientNotes() {
    this.patientService.getPatientNotes(this.patientId).subscribe(notes => {
      this.patientNotes = notes;
      this.buildPatientNotes(notes);
    }, (error) => {
      this.buildPatientNotes([]);
    });
  }

  async buildPatientNotes(patientNotes: PatientNotes[]) {
   if (patientNotes === null) {
      return;
    }

    if (patientNotes.length === 0) {
      this.offlinePatientNotes = await this.patientNoteStorageService.getPatientNotes(this.patientCaseNumber);
      patientNotes = this.offlinePatientNotes;
    } else {
      try {
        this.offlinePatientNotes = patientNotes;

        this.patientNoteStorageService.deleteAllByCaseByNumber(this.patientCaseNumber).then(() => {
          this.patientNoteStorageService.addPatientNotes(patientNotes);
        });
      } catch (error) {}
    }
  }

  createNote() {
    this.dialogConfig.data = {
      patientName: this.patientName,
      casenumber: this.patientCaseNumber,
    };
    const dialogReference = this.modalService.open(CreatePatientNoteDialog, this.dialogConfig);

    dialogReference.afterClosed().subscribe((note) => {
      if (!note) {
        return;
      }
      
      const token = this.authenticationService.decodeToken(
        localStorage.getItem('token')
      );
  
      var newNote = new PatientNoteItem()
      newNote.note = note;
      newNote.deviceId = this.deviceService.getDeviceId();
      newNote.stockLocationId = this.deviceService.getStockLocationId();
      newNote.patientId = this.patientId;
      newNote.userName = token.unique_name;
      newNote.caseNumber = this.patientCaseNumber;
      newNote.institutionId = this.deviceService.getInstitutionId();

      this.patientService.createPatientNote(newNote).subscribe(async result => {
        this.notify.success("Patient note sent");
        if (result) {
          this.getPatientNotes();
        } else {
          await this.savePendingPatientNote(newNote);
          this.getPatientNotes();
        }
      }, async (error) => {
        this.notify.success("Patient note sent");
        await this.savePendingPatientNote(newNote);
      });
    });
  }

  async savePendingPatientNote(newNote: PatientNoteItem): Promise<boolean> {
     var note = new PatientNotes();

     note.caseNumber = newNote.caseNumber;
     note.createdDateTime = new Date();
     note.note = newNote.note;
     note.username = newNote.userName;
     note.hasBeenSubmittedToSor = false;
     note.isSuccess = false;

     await this.offlinePatientNotes.unshift(note);

     this.patientNoteStorageService.deleteAllByCaseByNumber(this.patientCaseNumber).then(() => {
      this.patientNoteStorageService.addPatientNotes(this.offlinePatientNotes);
    });

    return true
  }

  ngOnDestroy(): void {
  }
}
